import { useMemo, useState, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Collapse,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import type { TeamsAvailability, TeamsChatPrefs, TeamsChatSummary } from '@atlas/shared';
import {
  CheckIcon,
  TeamsChevronDownIcon,
  TeamsChevronRightIcon,
  TeamsMoreIcon,
  TeamsMuteIcon,
  TeamsStarFilledIcon,
  TeamsStarIcon,
  TeamsUnmuteIcon,
} from '@atlas/ui';
import { useTeamsChatStore, type TeamsChatSection } from '../../stores/teamsChatStore';
import { TeamsAvatar } from './TeamsAvatar';
import { TeamsPresenceDot } from './TeamsPresenceDot';

/**
 * How many conversations a section shows before you ask for more, and how many
 * each ask adds — mirroring how Teams itself pages its chat list.
 *
 * Nothing is hidden permanently: "see more" walks the whole section, so a
 * conversation from months ago is a couple of clicks away rather than
 * unreachable. Teams gives each pair exactly one 1:1 chat, so for People "the
 * most recent 20 conversations" and "the most recent 20 people" are the same
 * set, and no de-duplication by person is needed.
 */
const SECTION_INITIAL_COUNT = 20;
const SECTION_PAGE_SIZE = 10;

/** One line of text that ellipsises rather than wrapping — the panel is ~320px wide. */
const ellipsis = {
  display: 'block',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  minWidth: 0,
} as const;

interface TeamsChatListProps {
  chats: TeamsChatSummary[];
  activeChatId?: string;
  isLoading: boolean;
  errorMessage?: string;
  /**
   * Availability by Entra object id. Absent entirely when the user's token
   * lacks `Presence.Read.All`, or when the lookup failed — either way no dots,
   * and no gap where they would have been.
   */
  presence?: Map<string, TeamsAvailability>;
  /** False unless the token carries `User.ReadBasic.All` — see `TeamsAvatar`. */
  avatarsAvailable?: boolean;
  onSelect: (chatId: string) => void;
  /** Toggle favourite/mute for one chat. Sends only the flag that changed. */
  onSetPrefs: (chatId: string, prefs: TeamsChatPrefs) => void;
  /** Clear one chat's unread badge without opening it. */
  onMarkRead: (chatId: string) => void;
  /** Clear every chat's unread badge, across all sections. */
  onMarkAllRead: () => void;
  /** Mute or unmute a whole section at once — the Groups header's menu. */
  onSetMuted: (chatIds: string[], muted: boolean) => void;
  onRetry: () => void;
}

/**
 * Purely presentational conversation list for the Teams sidebar. Fetches
 * nothing — loading, error and data all arrive as props.
 */
export function TeamsChatList({
  chats,
  activeChatId,
  isLoading,
  errorMessage,
  presence,
  avatarsAvailable,
  onSelect,
  onSetPrefs,
  onMarkRead,
  onMarkAllRead,
  onSetMuted,
  onRetry,
}: TeamsChatListProps) {
  const { t } = useTranslation();

  /**
   * Split into the two sections, preserving the server's newest-first order —
   * `filter` keeps it, so neither section needs re-sorting.
   *
   * Anything that is not a 1:1 counts as a group, `meeting` and `unknown`
   * included. A meeting chat has several participants and belongs with the
   * groups; leaving unknown types out of both sections would make conversations
   * silently vanish from the panel, which is worse than filing them slightly
   * wrong.
   */
  const { favorites, people, groups } = useMemo(() => {
    const pinned: TeamsChatSummary[] = [];
    const oneOnOne: TeamsChatSummary[] = [];
    const rest: TeamsChatSummary[] = [];
    for (const chat of chats) {
      // Favourite wins outright — a pinned chat lives in Favourites and nowhere
      // else, so the section counts still add up to the list the user has.
      if (chat.isFavorite) {
        pinned.push(chat);
        continue;
      }
      (chat.chatType === 'oneOnOne' ? oneOnOne : rest).push(chat);
    }
    // Each section is handed its full list — how much of it is on screen is
    // the section's own business, since only it knows how far the user has
    // paged.
    return { favorites: pinned, people: oneOnOne, groups: rest };
  }, [chats]);

  if (isLoading) {
    return (
      <Stack spacing={1.5} sx={{ p: 2 }} aria-busy aria-label={t('teams.panel.loading')}>
        {[0, 1, 2, 3, 4].map((row) => (
          <Stack key={row} spacing={0.5}>
            <Skeleton variant="text" width="60%" height={20} />
            <Skeleton variant="text" width="90%" height={16} />
          </Stack>
        ))}
      </Stack>
    );
  }

  if (errorMessage) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error" sx={{ mb: 1.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {t('teams.panel.errorTitle')}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5, wordBreak: 'break-word' }}>
            {errorMessage}
          </Typography>
        </Alert>
        <Button size="small" variant="outlined" onClick={onRetry} fullWidth>
          {t('teams.panel.retry')}
        </Button>
      </Box>
    );
  }

  if (chats.length === 0) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {t('teams.panel.noChats')}
        </Typography>
      </Box>
    );
  }

  return (
    // Owns its own scrolling rather than relying on an ancestor to provide it.
    // The drawer's Paper happens to scroll, so this looked fine there; the
    // standalone window has no such ancestor and simply clipped the list.
    // Scrolling here also keeps the sticky section headers working, since they
    // stick to this box rather than to whatever happens to be above it.
    <Box sx={{ width: '100%', flex: 1, minHeight: 0, overflowY: 'auto' }}>
      {/* Only when a dot is showing. A muted chat's unread state draws no dot,
          so offering this over a list of quiet rows would look like a no-op. */}
      {chats.some((c) => c.hasUnread && !c.isMuted) ? (
        <Button
          size="small"
          fullWidth
          startIcon={<CheckIcon size={16} aria-hidden />}
          onClick={onMarkAllRead}
          sx={{ justifyContent: 'flex-start', px: 1.5, borderBottom: 1, borderColor: 'divider' }}
        >
          {t('notifications.markAllRead')}
        </Button>
      ) : null}
      <ChatSection
        section="favorites"
        label={t('teams.panel.sectionFavorites')}
        chats={favorites}
        activeChatId={activeChatId}
        presence={presence}
        avatarsAvailable={avatarsAvailable}
        onSelect={onSelect}
        onSetPrefs={onSetPrefs}
        onMarkRead={onMarkRead}
      />
      <ChatSection
        section="people"
        label={t('teams.panel.sectionPeople')}
        chats={people}
        activeChatId={activeChatId}
        presence={presence}
        avatarsAvailable={avatarsAvailable}
        onSelect={onSelect}
        onSetPrefs={onSetPrefs}
        onMarkRead={onMarkRead}
      />
      <ChatSection
        section="groups"
        label={t('teams.panel.sectionGroups')}
        onSetMuted={onSetMuted}
        chats={groups}
        activeChatId={activeChatId}
        presence={presence}
        avatarsAvailable={avatarsAvailable}
        onSelect={onSelect}
        onSetPrefs={onSetPrefs}
        onMarkRead={onMarkRead}
      />
    </Box>
  );
}

interface ChatSectionProps {
  section: TeamsChatSection;
  label: string;
  chats: TeamsChatSummary[];
  activeChatId?: string;
  presence?: Map<string, TeamsAvailability>;
  avatarsAvailable?: boolean;
  onSelect: (chatId: string) => void;
  onSetPrefs: (chatId: string, prefs: TeamsChatPrefs) => void;
  onMarkRead: (chatId: string) => void;
  /**
   * Mute or unmute every conversation in this section. Absent means the header
   * gets no menu — only Groups has one, because it is the only section where
   * "silence the lot of them" is a thing people want.
   */
  onSetMuted?: (chatIds: string[], muted: boolean) => void;
}

/**
 * One collapsible group of conversations.
 *
 * An empty section renders nothing at all rather than a header over a void —
 * someone with no group chats should not be told they have a Groups section.
 */
function ChatSection({
  section,
  label,
  chats,
  activeChatId,
  presence,
  avatarsAvailable,
  onSelect,
  onSetPrefs,
  onMarkRead,
  onSetMuted,
}: ChatSectionProps) {
  const { t } = useTranslation();
  const collapsedSections = useTeamsChatStore((s) => s.collapsedSections);
  const toggleSection = useTeamsChatStore((s) => s.toggleSection);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  /**
   * Deliberately component state rather than the persisted store: how far you
   * paged is about this sitting, not a preference. Coming back to a panel
   * expanded to 80 rows because of something you did last week is worse than
   * starting from the top, and the newest conversations are at the top anyway.
   */
  const [visibleCount, setVisibleCount] = useState(SECTION_INITIAL_COUNT);

  if (chats.length === 0) return null;

  const visible = chats.slice(0, visibleCount);
  const hiddenCount = chats.length - visible.length;

  const isCollapsed = collapsedSections.includes(section);
  const contentId = `teams-chat-section-${section}`;
  /**
   * One toggle rather than a Mute-all and an Unmute-all sitting side by side:
   * with anything still making noise the useful action is to silence it, and
   * only once the whole section is quiet does the reverse mean anything.
   *
   * The action then touches ONLY the chats that need it, so muting a section
   * where nine of ten are already muted is one request, not ten.
   */
  const noisy = chats.filter((chat) => !chat.isMuted);
  const willMute = noisy.length > 0;
  const bulkTargets = (willMute ? noisy : chats).map((chat) => chat.id);

  return (
    <Box component="section">
      <ListItemButton
        onClick={() => toggleSection(section)}
        aria-expanded={!isCollapsed}
        aria-controls={contentId}
        sx={{
          px: 1.5,
          py: 0.75,
          gap: 0.5,
          // Sticky so the section a row belongs to stays identifiable while
          // scrolling — without it a long People list scrolls under an
          // unlabelled void.
          position: 'sticky',
          top: 0,
          zIndex: 1,
          bgcolor: 'background.paper',
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        {isCollapsed ? (
          <TeamsChevronRightIcon fontSize="small" aria-hidden />
        ) : (
          <TeamsChevronDownIcon fontSize="small" aria-hidden />
        )}
        <Typography
          variant="caption"
          sx={{ flex: 1, fontWeight: 700, letterSpacing: 0.6, textTransform: 'uppercase', ...ellipsis }}
        >
          {label}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
          {chats.length}
        </Typography>

        {/* Same nesting rules as the row's own menu button: `component="span"`
            because a <button> inside the header's button is invalid HTML, and
            `stopPropagation` because otherwise opening the menu would also
            collapse the section underneath it. */}
        {onSetMuted ? (
          <IconButton
            component="span"
            role="button"
            size="small"
            aria-label={t('teams.panel.sectionOptions', { name: label })}
            aria-haspopup="menu"
            aria-expanded={Boolean(menuAnchor)}
            onClick={(event: MouseEvent<HTMLElement>) => {
              event.stopPropagation();
              setMenuAnchor(event.currentTarget);
            }}
            onMouseDown={(event: MouseEvent<HTMLElement>) => event.stopPropagation()}
            sx={{ flexShrink: 0, p: 0.25 }}
          >
            <TeamsMoreIcon size={16} aria-hidden />
          </IconButton>
        ) : null}
      </ListItemButton>

      {onSetMuted ? (
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
          onClick={(event: MouseEvent<HTMLElement>) => event.stopPropagation()}
          slotProps={{ list: { dense: true } }}
        >
          <MenuItem
            onClick={() => {
              onSetMuted(bulkTargets, willMute);
              setMenuAnchor(null);
            }}
          >
            <ListItemIcon>
              {willMute ? (
                <TeamsMuteIcon size={18} aria-hidden />
              ) : (
                <TeamsUnmuteIcon size={18} aria-hidden />
              )}
            </ListItemIcon>
            <ListItemText>
              {willMute
                ? t('teams.panel.muteAll', { count: bulkTargets.length })
                : t('teams.panel.unmuteAll', { count: bulkTargets.length })}
            </ListItemText>
          </MenuItem>
        </Menu>
      ) : null}

      {/* `unmountOnExit` keeps a collapsed section's rows out of the tree
          entirely, so they cannot be reached by keyboard while hidden. */}
      <Collapse in={!isCollapsed} unmountOnExit>
        <List disablePadding id={contentId}>
          {visible.map((chat) => (
            <ChatRow
              key={chat.id}
              chat={chat}
              isActive={chat.id === activeChatId}
              presence={presence}
              avatarsAvailable={avatarsAvailable}
              onSelect={onSelect}
              onSetPrefs={onSetPrefs}
              onMarkRead={onMarkRead}
            />
          ))}
          {hiddenCount > 0 ? (
            <Button
              size="small"
              fullWidth
              onClick={() => setVisibleCount((count) => count + SECTION_PAGE_SIZE)}
              sx={{ justifyContent: 'flex-start', px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}
            >
              {t('teams.panel.seeMore', { count: Math.min(hiddenCount, SECTION_PAGE_SIZE) })}
            </Button>
          ) : null}
        </List>
      </Collapse>
    </Box>
  );
}

interface ChatRowProps {
  chat: TeamsChatSummary;
  isActive: boolean;
  presence?: Map<string, TeamsAvailability>;
  avatarsAvailable?: boolean;
  onSelect: (chatId: string) => void;
  onSetPrefs: (chatId: string, prefs: TeamsChatPrefs) => void;
  onMarkRead: (chatId: string) => void;
}

/**
 * One conversation.
 *
 * The message preview and the relative timestamp were both removed (#343): at
 * ~320px the three stacked lines read as clutter rather than information, and
 * the preview in particular was the noisiest thing in the panel. Ordering still
 * carries recency — the list is newest-first — so the time was telling the user
 * something the position already said.
 *
 * `lastUpdatedAt` is still fetched and still matters; it is what `hasUnread` is
 * computed from server-side. It is simply no longer rendered.
 */
function ChatRow({
  chat,
  isActive,
  presence,
  avatarsAvailable,
  onSelect,
  onSetPrefs,
  onMarkRead,
}: ChatRowProps) {
  const { t } = useTranslation();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const showMembers = chat.chatType === 'group' || chat.chatType === 'meeting';
  // Only 1:1 chats carry a `presenceUserId`. Group chats get no dot on
  // purpose — one marker cannot answer "who in this group is free".
  const availability = chat.presenceUserId ? presence?.get(chat.presenceUserId) : undefined;

  return (
    <ListItemButton
      selected={isActive}
      onClick={() => onSelect(chat.id)}
      alignItems="flex-start"
      sx={{
        display: 'block',
        px: 2,
        py: 1.25,
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
        {/* 1:1 only. A group has no single face, and `presenceUserId` — the one
            oid we hold per row — is only populated for one-to-ones. */}
        {chat.presenceUserId ? (
          <TeamsAvatar
            name={chat.title}
            entraOid={chat.presenceUserId}
            avatarsAvailable={avatarsAvailable}
            size={24}
          />
        ) : null}
        {availability ? <TeamsPresenceDot availability={availability} /> : null}
        <Typography
          variant="subtitle2"
          sx={{
            ...ellipsis,
            flex: 1,
            // Unread outranks active for weight: a chat you are looking at is
            // already marked by the row's `selected` background.
            fontWeight: chat.hasUnread || isActive ? 700 : 600,
          }}
          title={chat.title}
        >
          {chat.title}
        </Typography>
        {/* A muted chat shows a crossed-out bell INSTEAD of the unread dot, not
            beside it: the dot is the alert, and muting is a request not to be
            alerted. The icon is what tells the user why an active conversation
            has gone quiet — without it, mute looks like a bug. */}
        {chat.isMuted ? (
          <Box sx={{ flexShrink: 0, display: 'flex', color: 'text.disabled' }}>
            <TeamsMuteIcon size={14} aria-hidden />
            <Box component="span" sx={visuallyHidden}>
              {t('teams.panel.mutedChat')}
            </Box>
          </Box>
        ) : chat.hasUnread ? (
          /* Carries the accessible text too — the dot alone is invisible to a
             screen reader, and this row's only other cue is a font weight. */
          <Box
            sx={{
              flexShrink: 0,
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: 'error.main',
            }}
          >
            <Box component="span" sx={visuallyHidden}>
              {t('teams.panel.unreadChat')}
            </Box>
          </Box>
        ) : null}

        {/* `stopPropagation` on both handlers: this button lives inside the
            row's own button, so without it opening the menu would also open the
            conversation. `component="span"` for the same reason — a nested
            <button> is invalid HTML and browsers recover from it by moving the
            inner one out of the row. */}
        <IconButton
          component="span"
          role="button"
          size="small"
          aria-label={t('teams.panel.chatOptions', { name: chat.title })}
          aria-haspopup="menu"
          aria-expanded={Boolean(menuAnchor)}
          onClick={(event: MouseEvent<HTMLElement>) => {
            event.stopPropagation();
            setMenuAnchor(event.currentTarget);
          }}
          onMouseDown={(event: MouseEvent<HTMLElement>) => event.stopPropagation()}
          sx={{ flexShrink: 0, p: 0.25 }}
        >
          <TeamsMoreIcon size={16} aria-hidden />
        </IconButton>
      </Stack>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        // Every click inside stops here too — the menu renders in a portal, but
        // React's synthetic events still bubble through the tree it was created
        // in, which is this row.
        onClick={(event: MouseEvent<HTMLElement>) => event.stopPropagation()}
        slotProps={{ list: { dense: true } }}
      >
        {/* Disabled rather than hidden on an already-read chat: a menu whose
            items move around between openings is harder to use than one with a
            greyed row that says what state the chat is in. */}
        <MenuItem
          disabled={!chat.hasUnread}
          onClick={() => {
            onMarkRead(chat.id);
            setMenuAnchor(null);
          }}
        >
          <ListItemIcon>
            <CheckIcon size={18} aria-hidden />
          </ListItemIcon>
          <ListItemText>{t('teams.panel.markRead')}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            onSetPrefs(chat.id, { muted: !chat.isMuted });
            setMenuAnchor(null);
          }}
        >
          <ListItemIcon>
            {chat.isMuted ? (
              <TeamsUnmuteIcon size={18} aria-hidden />
            ) : (
              <TeamsMuteIcon size={18} aria-hidden />
            )}
          </ListItemIcon>
          <ListItemText>
            {chat.isMuted ? t('teams.panel.unmute') : t('teams.panel.mute')}
          </ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            onSetPrefs(chat.id, { favorite: !chat.isFavorite });
            setMenuAnchor(null);
          }}
        >
          <ListItemIcon>
            {chat.isFavorite ? (
              <TeamsStarFilledIcon size={18} aria-hidden />
            ) : (
              <TeamsStarIcon size={18} aria-hidden />
            )}
          </ListItemIcon>
          <ListItemText>
            {chat.isFavorite
              ? t('teams.panel.removeFavorite')
              : t('teams.panel.addFavorite')}
          </ListItemText>
        </MenuItem>
      </Menu>

      {showMembers ? (
        <Typography variant="caption" color="text.secondary" sx={{ ...ellipsis, mt: 0.25 }}>
          {t('teams.panel.memberCount', { count: chat.memberCount })}
        </Typography>
      ) : null}
    </ListItemButton>
  );
}
